/**
 * Shared expense calculation utilities used by both the Expenses tab and the
 * Home tab "Outstanding Expenses" card.  Centralising the logic here means
 * every surface uses the same `isLiveExpense` filter, the same settlement-aware
 * balance, and the same per-traveller attribution — so an edit/delete/settle
 * anywhere propagates correctly to every dependent view.
 */

import type { Expense, TripConfig, User } from '../types';
import { getCountryFlag } from './countryFlag';
import { getCurrencyFromLocation } from './currencyUtils';

/** The expense editor and its write validation must agree for legacy trip defaults. */
export function expenseBaseCurrency(trip: TripConfig | null | undefined): string {
  if (trip?.baseCurrency) return trip.baseCurrency;
  const location = trip?.homeCountry || trip?.travellers?.find(t => t.id === trip.ownerId)?.homeLocation;
  return getCurrencyFromLocation(location || '');
}

// ── Validity guard ─────────────────────────────────────────────────────────────

/** True only when an amount is a finite, positive number. No upper cap — weak
 *  currencies (VND, IDR, IRR, …) routinely push legitimate single expenses well
 *  past any "sane" ceiling, so we only guard against non-finite / non-positive
 *  values. Number.isFinite already rejects NaN/±Infinity. */
export function isValidAmount(v: unknown): boolean {
  return typeof v === 'number' && Number.isFinite(v) && v > 0;
}

/**
 * True if the expense should count toward trip balances, totals, and
 * per-person attribution. Source-removed rows retain their financial history;
 * only rows whose converted amount is missing or malformed are excluded.
 */
export function isLiveExpense(e: Expense): boolean {
  // itineraryDeleted no longer excludes an expense from totals — the money was
  // spent regardless of whether the linked itinerary entry still exists.
  // The card shows an informational badge; the settle tab still counts the row.
  return isValidAmount(e.targetAmount);
}

/** Historical identities are display-only; never substitute another traveller. */
export function expenseTraveller(id: string, travellers: User[]): User {
  return travellers.find(t => t.id === id)
    || { id, name: 'Former traveller', email: '', color: '', emoji: '', initials: '?', imgSrc: null };
}

// ── Per-share settlement model ─────────────────────────────────────────────────

export interface SettlePending {
  kind: 'settle' | 'unsettle';
  amount: number;
  markedBy: string;
  markedAt: string;
}

export interface SettleRow {
  e: Expense;
  share: number;       // display: in targetCurrency
  shareOrig: number;   // source of truth: in expense's original currency
  settled: number;     // display: in targetCurrency
  settledOrig: number; // source of truth: in expense's original currency
  remaining: number;   // display: share − settled, in targetCurrency
  pending?: SettlePending;
  debtorId: string;
  role: 'debtor' | 'creditor';
  dest?: { emoji: string; label: string };
}

export interface SettleGroupData {
  other: User;
  formerTraveller?: boolean;
  rows: SettleRow[];
  total: number;
}

type DestLite = {
  name: string;
  arrivalDate: string;
  departureDate: string;
  emoji?: string;
  country?: string;
};

/**
 * Map an expense to its destination for location chips.  Uses the linked
 * itinerary date.  Matching is departure-EXCLUSIVE so that a changeover day
 * is attributed to the arriving destination, not the departing one.
 */
export function destForExpense(e: Expense, destinations: DestLite[]) {
  const d = (e.importedItemDate || e.date || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return undefined;
  const within = (x: DestLite, inclusive: boolean) => {
    const a = (x.arrivalDate || '').slice(0, 10);
    const b = (x.departureDate || '').slice(0, 10);
    return a && b && d >= a && (inclusive ? d <= b : d < b);
  };
  const dest =
    destinations.find(x => within(x, false)) ||
    destinations.find(x => within(x, true));
  if (!dest) return undefined;
  return {
    emoji: getCountryFlag(dest.country || dest.name),
    label: `${dest.name}${dest.country ? ', ' + dest.country : ''}`,
  };
}

/**
 * Build the three Settle-tab sections for `userId`:
 *  - `oweGroups`    — amounts the user still owes each payer (remaining after settlements)
 *  - `owedGroups`   — amounts each debtor still owes the user
 *  - `settledGroups`— fully settled rows, for historical display
 */
export function buildSettleData(
  expenses: Expense[],
  userId: string,
  travellers: User[],
  destinations: DestLite[],
): { oweGroups: SettleGroupData[]; owedGroups: SettleGroupData[]; settledGroups: SettleGroupData[] } {
  const userById: Record<string, User> = Object.fromEntries(
    travellers.map(u => [u.id, u]),
  );
  const owe: Record<string, SettleRow[]>     = {};
  const owed: Record<string, SettleRow[]>    = {};
  const settled: Record<string, SettleRow[]> = {};
  const EPS = 0.01;

  const mkRow = (
    e: Expense,
    share: number,
    debtorId: string,
    role: 'debtor' | 'creditor',
  ): SettleRow => {
    const s = e.settlements?.[debtorId];
    // Conversion factor: how many targetCurrency units per original-currency unit.
    // Never re-fetched — always derived from the stored targetAmount and amount so
    // the ratio is self-consistent with the currently displayed total.
    const unit = e.amount ? e.targetAmount / e.amount : 1;
    const shareOrig = unit ? share / unit : 0;  // = e.amount / totalPeople

    // Determine how much the debtor has paid in the ORIGINAL currency.
    // Priority: new canonical settledOrig → legacy full flag → back-convert settled.
    // This means old records are airtight on first read, with no data migration needed.
    let settledOrig: number;
    if (s?.settledOrig !== undefined) {
      settledOrig = s.settledOrig;
    } else if ((s as any)?.full) {
      settledOrig = shareOrig;          // legacy: was fully cleared at write time
    } else {
      settledOrig = s?.settled ? s.settled / unit : 0;  // legacy: back-convert
    }
    settledOrig = Math.min(settledOrig, shareOrig);     // clamp — can't overpay

    const settledAmt = settledOrig * unit;
    return {
      e,
      share,
      shareOrig,
      settled: settledAmt,
      settledOrig,
      remaining: share - settledAmt,
      pending: s?.pending,
      debtorId,
      role,
      dest: destForExpense(e, destinations),
    };
  };

  expenses.filter(isLiveExpense).forEach(e => {
    const split = e.split?.length ? e.split : [e.paid_by];
    const n = split.length;
    if (n === 0) return;
    // Include any non-app people in the headcount so each app-user's share
    // reflects the true split (e.g. 4 members + 2 guests = 6-way split).
    const totalPeople = n + (e.extraSplitCount || 0);
    const share = e.targetAmount / totalPeople;

    // Current user owes a share (debtor)
    if (split.includes(userId) && userId !== e.paid_by) {
      const row = mkRow(e, share, userId, 'debtor');
      if (row.remaining > EPS) (owe[e.paid_by] ||= []).push(row);
      else (settled[e.paid_by] ||= []).push(row);
    }

    // Current user is owed (payer) — one row per other debtor
    if (userId === e.paid_by) {
      split.forEach(d => {
        if (d === e.paid_by) return;
        const row = mkRow(e, share, d, 'creditor');
        if (row.remaining > EPS) (owed[d] ||= []).push(row);
        else (settled[d] ||= []).push(row);
      });
    }
  });

  const toGroups = (
    map: Record<string, SettleRow[]>,
    useRemaining: boolean,
  ): SettleGroupData[] =>
    Object.entries(map)
      .map(([id, rows]) => ({
        // Membership controls access, not historical debts. Keep the ledger
        // identity even when its profile is no longer in the trip's roster.
        other: userById[id] || expenseTraveller(id, []),
        formerTraveller: !userById[id],
        rows,
        total: rows.reduce(
          (s, r) => s + (useRemaining ? r.remaining : r.settled),
          0,
        ),
      }));

  return {
    oweGroups:     toGroups(owe,     true),
    owedGroups:    toGroups(owed,    true),
    settledGroups: toGroups(settled, false),
  };
}

/**
 * Compute each traveller's attributed share of all live expenses — i.e. the
 * portion of total spend they are responsible for, independent of who paid or
 * whether any settlement has been confirmed.  Used for the "By travellers"
 * header breakdown.
 */
export function computeAttributedPerTraveller(
  expenses: Expense[],
  travellers: User[],
): Record<string, number> {
  const attributed: Record<string, number> = {};
  travellers.forEach(u => { attributed[u.id] = 0; });
  expenses.filter(isLiveExpense).forEach(e => {
    const split = e.split?.length ? e.split : [e.paid_by];
    const totalPeople = split.length + (e.extraSplitCount || 0);
    const share = e.targetAmount / totalPeople;
    split.forEach(uid => { attributed[uid] = (attributed[uid] || 0) + share; });
  });
  return attributed;
}

/**
 * Net remaining balance for the current user across all live expenses,
 * accounting for confirmed settlements.
 *
 *   +ve → others still owe you money ("you're owed")
 *   −ve → you still owe others  ("you owe")
 *
 * This is the canonical formula used by both Home and Expenses so the two
 * screens always agree.
 */
export function computeMyBalance(
  expenses: Expense[],
  userId: string,
  travellers: User[],
  destinations: DestLite[],
): number {
  const { oweGroups, owedGroups } = buildSettleData(
    expenses,
    userId,
    travellers,
    destinations,
  );
  const oweTotal  = oweGroups.reduce((s, g)  => s + g.total, 0);
  const owedTotal = owedGroups.reduce((s, g) => s + g.total, 0);
  return Math.round((owedTotal - oweTotal) * 100) / 100;
}

// ── Linked-expense deletion lock ──────────────────────────────────────────────

const money = (amount: number, currency: string): string => {
  try { return new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(amount); }
  catch { return `${amount.toFixed(2)} ${currency}`; }
};

/**
 * Why a linked expense must stay when its itinerary item is deleted, or null
 * when it can go. Someone other than the person deleting has settled (or
 * marked as paid, awaiting confirmation) against it: deleting it would erase
 * money that actually changed hands. Owner's rule, 2026-09-26.
 */
export function linkedExpenseLock(
  expense: Pick<Expense, 'settlements' | 'targetCurrency'> | undefined,
  userId: string,
  travellers: Pick<User, 'id' | 'name'>[],
): string | null {
  if (!expense) return null;
  const parts = Object.entries(expense.settlements || {})
    .filter(([id, s]) => id !== userId && ((s.settled || 0) > 0 || s.pending?.kind === 'settle'))
    .map(([id, s]) => {
      const name = (travellers.find(t => t.id === id)?.name || 'Someone').split(' ')[0];
      return (s.settled || 0) > 0
        ? `${name} has already settled ${money(s.settled, expense.targetCurrency)}`
        : `${name} has marked ${money(s.pending!.amount, expense.targetCurrency)} as paid`;
    });
  if (!parts.length) return null;
  const list = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}`;
  return `The expense will stay: ${list} against it.`;
}
